import { useTranslation } from "@redotech/redo-i18n";
import { assertNever } from "@redotech/util/type";
import { JSXElementConstructor, memo, ReactNode } from "react";
import { RedoBadge } from "../badge/redo-badge";
import { Card, CardSize } from "../card";
import { Flex } from "../flex";
import { LoadingRedoAnimation } from "../loading-redo-animation";
import { Text } from "../text";
import { InfoTooltip } from "../tooltip/info-tooltip";
import * as settingsCardCss from "./settings-card.module.css";

export enum ChannelConnectionState {
  ACTIVE = "active",
  CONNECTED = "connected",
  PENDING = "pending",
  UNVERIFIED = "unverified",
  NOT_CONNECTED = "not_connected",
  LOADING = "loading",
}

export type SettingsCardWidth = "half" | "third" | "full";

export const SettingsCard = memo(function SettingsCard({
  name,
  description,
  Logo,
  SecondaryLogo,
  connectionState,
  bottomContent,
  buttonSection,
  customBackgroundColor,
  customBorderColor,
  customBorderRadius,
  cardWidth = "half",
  height,
  infoTooltip,
  onCardClick,
  rawLogo,
  isBeta,
  "data-dd-action-name": actionName,
}: {
  "data-dd-action-name"?: string;
  name: string;
  description: string;
  Logo?: JSXElementConstructor<any>;
  SecondaryLogo?: JSXElementConstructor<any>;
  connectionState?: ChannelConnectionState;
  buttonSection?: JSX.Element;
  bottomContent?: JSX.Element;
  customBackgroundColor?: string;
  customBorderColor?: string;
  customBorderRadius?: number;
  cardWidth?: SettingsCardWidth;
  height?: "short";
  infoTooltip?: ReactNode;
  onCardClick?: () => void;
  rawLogo?: boolean;
  isBeta?: boolean;
}) {
  const { t } = useTranslation();
  const selectButton =
    connectionState === ChannelConnectionState.LOADING ? (
      <div className={settingsCardCss.spinnerContainer}>
        <LoadingRedoAnimation />
      </div>
    ) : (
      <div className={settingsCardCss.buttonContainer}>{buttonSection}</div>
    );

  const getContentClassName = () => {
    let baseClass;
    // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
    switch (cardWidth) {
      case "third":
        baseClass = settingsCardCss.contentThird;
        break;
      case "full":
        baseClass = settingsCardCss.contentFull;
        break;
      default:
        baseClass = settingsCardCss.content;
    }
    const classes = [baseClass];
    if (height === "short") {
      classes.push(settingsCardCss.heightShort);
    }
    if (onCardClick) {
      classes.push(settingsCardCss.clickable);
    }
    return classes.join(" ");
  };

  const isShort = height === "short";

  return (
    <Card
      className={getContentClassName()}
      customBackgroundColor={customBackgroundColor}
      customBorderColor={customBorderColor}
      customBorderRadius={customBorderRadius}
      data-dd-action-name={actionName}
      onClick={onCardClick}
      px={isShort ? "xl" : undefined}
      py={isShort ? "lg" : undefined}
      size={CardSize.MEDIUM}
    >
      <Flex className={settingsCardCss.contentWrapper} dir="column">
        <Flex align="flex-start" justify="space-between">
          <Flex className={settingsCardCss.titleWrapper} gap="xl">
            <Flex dir="column">
              {Logo &&
                (rawLogo ? (
                  <Logo />
                ) : (
                  <div className={settingsCardCss.imageContainer}>
                    <Logo className={settingsCardCss.image} />
                  </div>
                ))}
              {SecondaryLogo && (
                <div className={settingsCardCss.imageContainer}>
                  <SecondaryLogo className={settingsCardCss.image} />
                </div>
              )}
            </Flex>
            <Flex dir="column" gap="xs">
              <Flex
                className={settingsCardCss.titleContent}
                dir="column"
                gap="xs"
              >
                <Flex align="center" gap="xs">
                  <Text fontSize="sm" fontWeight="semibold" textColor="primary">
                    {name}
                  </Text>
                  {isBeta && (
                    <RedoBadge
                      color="orange"
                      size="xs"
                      text={t("Beta", { context: "feature badge" })}
                    />
                  )}
                  {infoTooltip && (
                    <InfoTooltip
                      boxSize={14}
                      tooltip={{ title: infoTooltip }}
                    />
                  )}
                </Flex>
                {connectionState !== undefined && (
                  <div>
                    <ConnectionStatePill state={connectionState} />
                  </div>
                )}
              </Flex>
              {!isShort && description && (
                <Text fontSize="xs">{description}</Text>
              )}
            </Flex>
          </Flex>
          {isShort && selectButton}
        </Flex>
        {bottomContent}
        {!isShort && (
          <div className={settingsCardCss.selectButton}>{selectButton}</div>
        )}
      </Flex>
    </Card>
  );
});

const ConnectionStatePill = memo(function ConnectionStatePill({
  state,
}: {
  state: ChannelConnectionState;
}) {
  const { t } = useTranslation();
  switch (state) {
    case ChannelConnectionState.ACTIVE:
      return (
        <RedoBadge
          color="success"
          segmentLeading={{
            type: "icon",
            Icon: () => <div className={settingsCardCss.activeDot} />,
          }}
          size="xs"
          text={t("Active", { context: "settings card status" })}
        />
      );
    case ChannelConnectionState.CONNECTED:
      return (
        <RedoBadge
          color="success"
          segmentLeading={{
            type: "icon",
            Icon: () => <div className={settingsCardCss.activeDot} />,
          }}
          size="xs"
          text={t("Connected", { context: "settings card status" })}
        />
      );
    case ChannelConnectionState.PENDING:
      return (
        <RedoBadge
          color="blue"
          size="xs"
          text={t("Pending", { context: "settings card status" })}
        />
      );
    case ChannelConnectionState.UNVERIFIED:
      return (
        <RedoBadge
          color="error"
          size="xs"
          text={t("Unverified", { context: "settings card status" })}
        />
      );
    case ChannelConnectionState.NOT_CONNECTED:
      return (
        <RedoBadge
          color="gray"
          size="xs"
          text={t("Not connected", { context: "settings card status" })}
        />
      );
    case ChannelConnectionState.LOADING:
      return null;
    default:
      return assertNever(state);
  }
});
